import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as contentApi from '@/lib-backend/methods/content';
import { FaqItem, FaqItemSummary, SiteContent, SiteContentSummary, SocialLink } from '@/lib-backend';

@Injectable({ providedIn: 'root' })
export class SiteContentService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly items = signal<SiteContentSummary[]>([]);
    readonly loading = signal(false);

    readonly faqItems = signal<FaqItem[]>([]);
    readonly faqLoading = signal(false);

    readonly socialLinks = signal<SocialLink[]>([]);
    readonly socialLinksLoading = signal(false);

    private currentRealmId: string | null = null;

    async loadForRealm(realmId: string): Promise<void> {
        this.currentRealmId = realmId;
        this.loading.set(true);
        try {
            const resp = await this.api.client.call(
                contentApi.listSiteContent({ RealmId: realmId })
            );
            this.items.set(resp.Items);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async loadFaqsForRealm(realmId: string): Promise<void> {
        this.faqLoading.set(true);
        try {
            const listResp = await this.api.client.call(
                contentApi.listFaqs({ RealmId: realmId, Category: null })
            );
            const fullItems = await Promise.all(
                listResp.Items.map(async (summary) => {
                    const resp = await this.api.client.call(
                        contentApi.getFaqItem({ RealmId: realmId, FaqId: summary.Id })
                    );
                    if (resp.Result === 'Ok') return resp.Item;
                    return { ...summary, Message: '' } as FaqItem;
                })
            );
            this.faqItems.set(fullItems);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.faqLoading.set(false);
        }
    }

    async loadSocialLinksForRealm(realmId: string): Promise<void> {
        this.socialLinksLoading.set(true);
        try {
            const resp = await this.api.client.call(
                contentApi.getSocialLinks({ RealmId: realmId })
            );
            this.socialLinks.set(resp.Links);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.socialLinksLoading.set(false);
        }
    }

    async getContent(realmId: string, key: string): Promise<SiteContent | null> {
        try {
            const resp = await this.api.client.call(
                contentApi.getSiteContent({ RealmId: realmId, Key: key })
            );
            if (resp.Result === 'Ok') return resp.Entry;
            return null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async addContent(params: contentApi.AddSiteContentRequest): Promise<contentApi.AddSiteContentResult> {
        try {
            const resp = await this.api.client.call(contentApi.addSiteContent(params));
            if (resp.Result === 'Ok' && this.currentRealmId === params.RealmId) {
                await this.loadForRealm(params.RealmId);
            }
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }

    async updateContent(params: contentApi.UpdateSiteContentRequest): Promise<contentApi.UpdateSiteContentResult> {
        try {
            const resp = await this.api.client.call(contentApi.updateSiteContent(params));
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }

    async addFaqItem(params: contentApi.AddFaqItemRequest): Promise<FaqItem | null> {
        try {
            const resp = await this.api.client.call(contentApi.addFaqItem(params));
            if (this.currentRealmId === params.RealmId) {
                await this.loadFaqsForRealm(params.RealmId);
            }
            return resp.FaqItem;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async getFaqItem(realmId: string, faqId: string): Promise<FaqItem | null> {
        try {
            const resp = await this.api.client.call(
                contentApi.getFaqItem({ RealmId: realmId, FaqId: faqId })
            );
            if (resp.Result === 'Ok') return resp.Item;
            return null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async updateFaqItem(params: contentApi.UpdateFaqItemRequest): Promise<contentApi.UpdateFaqItemResult> {
        try {
            const resp = await this.api.client.call(contentApi.updateFaqItem(params));
            if (resp.Result === 'Ok' && this.currentRealmId === params.RealmId) {
                await this.loadFaqsForRealm(params.RealmId);
            }
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }

    async saveSocialLinks(realmId: string, links: SocialLink[]): Promise<boolean> {
        try {
            const resp = await this.api.client.call(
                contentApi.setSocialLinks({ RealmId: realmId, Links: links })
            );
            this.socialLinks.set(resp.Links);
            return true;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return false;
        }
    }
}
